// "Familie" — was die Angehörigen tun, und ob es beim Bewohner ankommt.
//
// Fast alles hier stammt aus Tabellen, die es ohnehin schon gab: Nachrichten,
// Anrufe, Erinnerungen, Kontakte. Nur die App-Öffnungen kommen aus usage_events.

import React, { useState } from "react";
import { Histogram, ProgressList, StatCard } from "./BreakdownPanel";
import { formatCount, formatDuration } from "./analyticsApi";

function identity(keys) {
  return Object.fromEntries(keys.map((key) => [key, key]));
}

/** Eine Kachel, drei Zahlen. Die drei Anrufarten gehören zusammen und je eine
 *  eigene Kachel gäbe ihnen dreimal so viel Gewicht wie den Nachrichten. */
function SplitStatCard({ label, parts }) {
  return (
    <div className="stat-card stat-card-wide">
      <div className="stat-label">{label}</div>
      <div className="stat-split">
        {parts.map((part) => (
          <div key={part.label}>
            <div className="stat-split-value">{part.value}</div>
            <div className="stat-split-label">{part.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const MODE_LABELS = { call: "Anrufe", video_call: "Videoanrufe", announcement: "Livedurchsagen" };
const DIRECTION_LABELS = { incoming: "Eingehend", outgoing: "Ausgehend" };

function Segmented({ options, value, onChange }) {
  return (
    <div className="segmented">
      {Object.entries(options).map(([key, label]) => (
        <button key={key} className={key === value ? "active" : ""} onClick={() => onChange(key)}>
          {label}
        </button>
      ))}
    </div>
  );
}

/** Dauer und Tageszeit für eine Anrufart. Beides beantwortet dieselbe Frage —
 *  wie ein Anruf dieser Art aussieht — und gehört deshalb in eine Karte. */
function CallShapeCard({ byMode }) {
  const [mode, setMode] = useState("call");
  const selected = byMode[mode];

  return (
    <div className="card">
      <div className="card-title">
        Anrufe, Videoanrufe und Livedurchsagen
        <Segmented options={MODE_LABELS} value={mode} onChange={setMode} />
      </div>
      <div className="detail-hist-label">
        Dauer · {formatCount(selected.connected)} von {formatCount(selected.total)} verbunden
      </div>
      <ProgressList
        rows={selected.by_duration}
        labels={identity(Object.keys(selected.by_duration))}
        ordered
      />
      <div className="detail-hist-label" style={{ marginTop: 20 }}>
        Nach Tageszeit
      </div>
      <Histogram hours={selected.by_hour} />
    </div>
  );
}

/** Ausgang, getrennt nach Richtung. Eingehend heißt: jemand hat den Bewohner
 *  angerufen. Ausgehend: der Bewohner hat angerufen — dort ist "selbst
 *  aufgelegt" ein eigener Ausgang und keine Ablehnung. */
function CallOutcomeCard({ byDirection }) {
  const [direction, setDirection] = useState("incoming");
  const selected = byDirection[direction];

  return (
    <div className="card">
      <div className="card-title">
        Ausgang · Anrufe und Videoanrufe
        <Segmented options={DIRECTION_LABELS} value={direction} onChange={setDirection} />
      </div>
      <div className="detail-hist-label">
        {formatCount(selected.total)}{" "}
        {direction === "incoming" ? "eingehend" : "ausgehend"}
      </div>
      <ProgressList
        rows={selected.outcomes}
        labels={identity(Object.keys(selected.outcomes))}
        ordered
      />
    </div>
  );
}

/** Rangliste ohne Balken und ohne Zahl: welche Vorlagen angelegt wurden, in
 *  welcher Kategorie. Die Menge steht als Summe in der Überschrift. */
function Leaderboard({ rows, labelOf, noteOf }) {
  if (!rows.length) return <div className="empty-note">Keine Daten in diesem Zeitraum.</div>;
  return (
    <div>
      {rows.map((row, index) => (
        <div key={labelOf(row)} className="lb-row lb-row-plain">
          <div className={`lb-rank ${index < 3 ? "lb-rank-top" : ""}`}>{index + 1}</div>
          <div className="lb-name">{labelOf(row)}</div>
          <div className="lb-value">{noteOf ? noteOf(row) : ""}</div>
        </div>
      ))}
    </div>
  );
}

export default function FamilyPanel({ data }) {
  if (!data) return <div className="empty-note">Keine Familiendaten.</div>;
  const { messages, calls, planning, contacts, app } = data;

  return (
    <div className="stack">
      <div className="stat-grid">
        <StatCard label="App geöffnet" value={formatCount(app.opens)} />
        <StatCard label="Nachrichten" value={formatCount(messages.total)} />
        <SplitStatCard
          label="Anrufe"
          parts={[
            { label: "Anrufe", value: formatCount(calls.audio) },
            { label: "Videoanrufe", value: formatCount(calls.video) },
            { label: "Livedurchsagen", value: formatCount(calls.announcements) },
          ]}
        />
        <StatCard label="Erinnerungen" value={formatCount(planning.reminders)} />
        <StatCard label="Kalendereinträge" value={formatCount(planning.events)} />
        <StatCard label="Kontakte" value={formatCount(contacts.total)} />
      </div>

      <div className="two-col">
        <div className="card">
          <div className="card-title">Nachrichten nach Typ</div>
          <ProgressList rows={messages.by_kind} labels={identity(Object.keys(messages.by_kind))} />
        </div>
        <div className="card">
          <div className="card-title">Nachrichten nach Tageszeit</div>
          <Histogram hours={messages.by_hour} />
        </div>
      </div>

      <div className="two-col">
        <CallOutcomeCard byDirection={calls.by_direction} />
        <CallShapeCard byMode={calls.by_mode} />
      </div>

      <div className="card">
        <div className="card-title">App-Nutzung</div>
        <div className="stat-grid stat-grid-3">
          <StatCard label="Geöffnet" value={formatCount(app.opens)} />
          <StatCard label="Gesamtzeit" value={formatDuration(app.total_ms)} />
          <StatCard label="Median je Besuch" value={formatDuration(app.median_ms)} />
        </div>
        <div className="detail-hist-label">Geöffnet nach Tageszeit</div>
        <Histogram hours={app.by_hour} />
      </div>

      <div className="card">
        <div className="card-title">
          Aktivierungen angelegt
          <span style={{ textTransform: "none", fontWeight: 600 }}>{formatCount(planning.routines)} gesamt</span>
        </div>
        <Leaderboard
          rows={planning.routines_by_kind}
          labelOf={(r) => r.name}
          noteOf={(r) => r.category}
        />
      </div>
    </div>
  );
}
